/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AccessControlPage.tsx
 🗓️ Created: 2025-12-06
 🧩 Description:
    Access Control page - Lists and manages IP whitelist/blacklist
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useRef } from 'react';
import { Shield, Play, RefreshCw, Unlock } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useAccessControlList } from './hooks/useAccessControlList';
import { AccessControlModal } from './components/AccessControlModal';
import { deleteAccessControlEntry, applyAccessControlRules, syncFail2BanIPs, unbanFail2BanIP, clearExpiredAccessControlEntries } from './api/accessControlApi';
import { Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import type { AccessControlEntry } from './types';

export default function AccessControlPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AccessControlEntry | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    entry: AccessControlEntry | null;
  }>({
    isOpen: false,
    entry: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isUnbanning, setIsUnbanning] = useState<string | null>(null);

  const {
    entries,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useAccessControlList();

  // Auto-sync Fail2Ban IPs on page load
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    if (hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    const autoSync = async () => {
      try {
        await syncFail2BanIPs();
        refresh();
      } catch {
        // Silent fail — manual sync button is still available
      }
    };
    autoSync();
  }, [refresh]);

  const handleAdd = useCallback(() => {
    setEditingEntry(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((entry: AccessControlEntry) => {
    setEditingEntry(entry);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback((entry: AccessControlEntry) => {
    setDeleteModal({ isOpen: true, entry });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.entry) return;

    setIsDeleting(true);
    try {
      await deleteAccessControlEntry(deleteModal.entry.id);
      showSuccess(t('accessControl.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, entry: null });
      refresh();

      // Apply changes to the system firewall
      try {
        await applyAccessControlRules();
        showSuccess(t('accessControl.messages.appliedToFirewall'));
      } catch (applyError) {
        // Show warning but don't fail the delete operation
        showError(t('accessControl.messages.applyError') + ': ' + (applyError instanceof Error ? applyError.message : ''));
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : t('accessControl.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.entry, refresh, showSuccess, showError, t]);

  const handleApplyRules = useCallback(async () => {
    setIsApplying(true);
    try {
      await applyAccessControlRules();
      showSuccess(t('accessControl.messages.appliedToFirewall'));
    } catch (error) {
      showError(t('accessControl.messages.applyError') + ': ' + (error.message || ''));
    } finally {
      setIsApplying(false);
    }
  }, [showSuccess, showError, t]);

  const handleModalSuccess = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleSyncFail2Ban = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await syncFail2BanIPs();
      console.log('[Fail2Ban Sync] Full result:', JSON.stringify(result, null, 2));
      showSuccess(`${t('accessControl.messages.syncSuccess')}: ${result.data.total} IPs (${result.data.added} ${t('accessControl.messages.newEntries')})`);
      refresh();
    } catch (error) {
      console.error('[Fail2Ban Sync] Error:', error);
      showError(error.message || t('accessControl.messages.syncError'));
    } finally {
      setIsSyncing(false);
    }
  }, [showSuccess, showError, t, refresh]);

  const handleClearExpired = useCallback(async () => {
    setIsClearing(true);
    try {
      const result = await clearExpiredAccessControlEntries();
      showSuccess(result.message || t('accessControl.messages.clearExpiredSuccess', 'Expired entries cleared successfully'));
      refresh();
    } catch (error: any) {
      showError(error.message || t('accessControl.messages.clearExpiredError', 'Failed to clear expired entries'));
    } finally {
      setIsClearing(false);
    }
  }, [showSuccess, showError, t, refresh]);

  const handleUnban = useCallback(async (entry: AccessControlEntry) => {
    setIsUnbanning(entry.ipAddress);
    try {
      await unbanFail2BanIP(entry.ipAddress);
      showSuccess(t('accessControl.messages.unbanSuccess', { ip: entry.ipAddress }));
      refresh();
    } catch (error) {
      showError(error.message || t('accessControl.messages.unbanError'));
    } finally {
      setIsUnbanning(null);
    }
  }, [showSuccess, showError, t, refresh]);

  const renderActions = useCallback(
    (entry: AccessControlEntry) => (
      <div className="flex items-center gap-2">
        {entry.source === 'fail2ban' && (
          <SimpleTooltip content={t('accessControl.unban')} position="top">
            <button
              onClick={() => handleUnban(entry)}
              disabled={isUnbanning === entry.ipAddress}
              className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 disabled:opacity-50"
            >
              <Unlock className={`h-4 w-4 text-green-600 dark:text-green-400 ${isUnbanning === entry.ipAddress ? 'animate-spin' : ''}`} />
            </button>
          </SimpleTooltip>
        )}

        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            onClick={() => handleEdit(entry)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            onClick={() => handleDelete(entry)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDelete, handleUnban, isUnbanning, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          title={t('accessControl.title')}
          titleIcon={<Shield className="h-5 w-5 text-primary" />}
          data={entries}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onSearch={handleSearch}
          onColumnToggle={handleColumnToggle}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          emptyMessage={t('accessControl.noEntriesFound')}
          searchPlaceholder={t('accessControl.searchPlaceholder')}
          renderActions={renderActions}
          addButtonText={t('common.add')}
          onAdd={handleAdd}
          actions={
            <div className="flex items-center gap-2">
              <SimpleTooltip content={t('accessControl.syncFail2Ban')} position="top">
                <Button
                  onClick={handleSyncFail2Ban}
                  disabled={isSyncing || loading}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? t('common.syncing') : t('accessControl.syncFail2Ban')}
                </Button>
              </SimpleTooltip>
              <SimpleTooltip content={t('accessControl.applyRules')} position="top">
                <Button
                  onClick={handleApplyRules}
                  disabled={isApplying || loading}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Play className={`h-4 w-4 ${isApplying ? 'animate-spin' : ''}`} />
                  {isApplying ? t('common.applying') : t('accessControl.apply')}
                </Button>
              </SimpleTooltip>
              <SimpleTooltip content={t('accessControl.clearExpired', 'Clear Expired')} position="top">
                <Button
                  onClick={handleClearExpired}
                  disabled={isClearing || loading}
                  variant="outline"
                  size="sm"
                  className="gap-2 text-red-400 border-border hover:bg-red-950/20 hover:text-red-300"
                >
                  <Trash2 className={`h-4 w-4 ${isClearing ? 'animate-spin' : ''}`} />
                  {isClearing ? t('common.clearing', 'Clearing...') : t('accessControl.clearExpired', 'Clear Expired')}
                </Button>
              </SimpleTooltip>
            </div>
          }
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll'),
          }}
        />
      }
      formView={null}
    >
      <AccessControlModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingEntry(null);
        }}
        onSuccess={handleModalSuccess}
        entry={editingEntry}
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, entry: null })}
        onConfirm={handleDeleteConfirm}
        title={t('accessControl.deleteModal.title')}
        message={t('accessControl.deleteModal.message', {
          name: deleteModal.entry?.name || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
